import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "flex items-center gap-6" }
const _hoisted_2 = { class: "flex items-center gap-6" }
const _hoisted_3 = { class: "flex items-center gap-4" }
const _hoisted_4 = { class: "flex items-center" }
const _hoisted_5 = { class: "flex justify-end flex-1 gap-2" }
const _hoisted_6 = { class: "flex items-center gap-4" }

import { uniq } from 'lodash';
import { computed, inject, Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useGetInspectionQuery } from '@/entities/inspection/hooks';
import { useGetServicesQuery } from '@/entities/services/hooks';
import { useGetUserQuery } from '@/entities/user/hooks';
import {
  useGetInspectionGroupsQuery,
  useGetInspectionGroupQuery, useDeleteInspectionGroupMutation,
} from '@/entities/inspection-group/hooks';

import {
  useGetDeviceQuery,
} from '@/entities/device/hooks';

import { FormatDateInjection } from '@/plugins/date-fns';
import { InspectionGroup } from '@/entities/inspection-group/types';

import { SUBNAVIGATION_HEIGHT } from '@/entities/app/components/navigation/navigation.styled';

import {
  NkText,
  NkButton,
  NkSelect,
  NkOption,
  NkGroup,
  NkProfile,
  NkHeading,
  NkBadge,
  NkInlineModal,
} from '@/core/components';

import NkStatusBadge from '@/entities/status/components/StatusBadge.vue';
import NkInspectionGroupCommentUpdateForm
  from '@/entities/inspection-group/components/form/InspectionGroupCommentUpdateForm.vue';
import NkInspectionEditForm from '@/entities/inspection/components/form/InspectionEditForm.vue';
import NkInspectionPDFForm from '@/entities/inspection/components/form/InspectionPDFForm.vue';
import NkInspectionPDFDownload from '@/entities/inspection/components/InspectionPDFDownload.vue';
import InspectionZIPForm from '@/entities/inspection/components/form/InspectionZIPForm.vue';
import { ElNotification } from 'element-plus';
import { useI18n } from 'vue-i18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'SubNavigation',
  setup(__props) {

const { t } = useI18n();

const route = useRoute();
const router = useRouter();

const $format: FormatDateInjection = inject('$format') as FormatDateInjection;

const { mutateAsync } = useDeleteInspectionGroupMutation();

const inspectionQueryData: Ref = computed(() => ({ id: Number(route.params.inspection) }));
const hasInspectionQueryData = computed(() => !!inspectionQueryData.value.id);
const { data: inspection } = useGetInspectionQuery(inspectionQueryData, {
  enabled: hasInspectionQueryData,
});

const inspectionGroupData: Ref = computed(() => ({ id: inspection.value?.inspectionGroup }));
const hasInspectionGroupData = computed(() => !!inspectionGroupData.value.id);
const { data: currentInspectionGroup } = useGetInspectionGroupQuery(inspectionGroupData, {
  enabled: hasInspectionGroupData,
});


const currentDeviceData: Ref = computed(() => ({ id: currentInspectionGroup.value?.device }));
const hasDeviceId = computed(() => !!currentInspectionGroup.value?.id);
const { data: currentDevice } = useGetDeviceQuery(currentDeviceData, {
  enabled: hasDeviceId,
});

const inspectionGroupsIds = computed(() => currentDevice.value?.inspectionGroups || []);
const hasInspectionGroupsIds = computed(() => !!inspectionGroupsIds.value.length);
const inspectionGroupsQueryFilters = computed(() => ({
  ids: [...inspectionGroupsIds.value],
  'exists[inspections]': true,
}));
const { data: inspectionGroups } = useGetInspectionGroupsQuery({
  enabled: hasInspectionGroupsIds,
}, inspectionGroupsQueryFilters);

const inspectionGroupUserData: Ref = computed(() => ({ id: currentInspectionGroup.value?.user }));
const hasInspectionGroupUserData = computed(() => !!currentInspectionGroup.value?.user);
const { data: user } = useGetUserQuery(inspectionGroupUserData, {
  enabled: hasInspectionGroupUserData,
});

const inspectionGroupServicesIds = computed(() => uniq(inspectionGroups.value?.map(o => o.service)));
const hasInspectionGroupServicesIds = computed(() => !!inspectionGroupServicesIds.value.length);
const inspectionGroupServicesQueryFilters: Ref = computed(() => ({ ids: [...inspectionGroupServicesIds.value] }));
const { data: services } = useGetServicesQuery({
  enabled: hasInspectionGroupServicesIds,
}, inspectionGroupServicesQueryFilters);

const getCurrentInspectionGroup = computed(() => ({
  ...currentInspectionGroup.value,
  user,
  service: services.value?.find(s => s.id === currentInspectionGroup.value?.service),
}));

const getInspectionGroups = computed(() => inspectionGroups.value?.filter(inspectionGroup => inspectionGroup.device === currentInspectionGroup.value?.device).map(ig => ({
  ...ig,
  service: services.value?.find(s => s.id === ig.service),
  date: $format(ig.date),
})) || []);

const canExportReport = computed(() => getCurrentInspectionGroup.value?.canGenerateReport);

const handleInspectionGroupChange = (value: InspectionGroup) => {
  router.push({
    name: 'visionair-inspection',
    params: {
      inspection: value.lastInspectionFromFirstDevice,
    },
  });
};

const handleInspectionGroupDelete = () => {
  mutateAsync(
    { id: inspectionGroupData.value.id },
    { onSuccess: () => {
        ElNotification({
          type: 'success',
          position: 'bottom-right',
          message: t('inspection-group.notification.delete.message'),
          title: t('inspection-group.notification.delete.title'),
        })
      } })
    .then(() => {
      router.push({
        name: 'visionair-device',
        params: {
          device: currentInspectionGroup.value?.device,
        },
      });
    });
};

// const isPDFModalVisible = ref(false);


return (_ctx: any,_cache: any) => {
  const _component_el_popconfirm = _resolveComponent("el-popconfirm")!

  return (_openBlock(), _createElementBlock("div", {
    class: "p-5 bg-gray-50",
    style: _normalizeStyle({minHeight: _unref(SUBNAVIGATION_HEIGHT)})
  }, [
    _createVNode(_unref(NkGroup), {
      position: "apart",
      class: "h-full"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(NkGroup), { spacing: 5 }, {
          default: _withCtx(() => [
            _createVNode(_unref(NkHeading), { type: "small-italic" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('misc.inspection')), 1)
              ]),
              _: 1
            }),
            (_unref(inspectionGroupData) && _unref(getInspectionGroups)?.length)
              ? (_openBlock(), _createBlock(_unref(NkSelect), {
                  key: 0,
                  class: "w-[500px]",
                  "value-key": "id",
                  "model-value": _unref(inspectionGroupData).id,
                  "onUpdate:modelValue": handleInspectionGroupChange
                }, {
                  inside: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                      _createElementVNode("div", _hoisted_2, [
                        _createVNode(_unref(NkText), { uppercase: "" }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref($format)(_unref(currentInspectionGroup)?.date)), 1)
                          ]),
                          _: 1
                        })
                      ])
                    ])
                  ]),
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(getInspectionGroups), (group) => {
                      return (_openBlock(), _createBlock(_unref(NkOption), {
                        key: group.id,
                        label: `Test - ${group.id}`,
                        value: group
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_3, [
                            _createElementVNode("div", _hoisted_4, [
                              _createVNode(_unref(NkText), { uppercase: "" }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(group.date), 1)
                                ]),
                                _: 2
                              }, 1024)
                            ]),
                            _createElementVNode("div", _hoisted_5, [
                              _createVNode(_unref(NkBadge), { size: "sm" }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(group?.service?.name), 1)
                                ]),
                                _: 2
                              }, 1024)
                            ])
                          ])
                        ]),
                        _: 2
                      }, 1032, ["label", "value"]))
                    }), 128))
                  ]),
                  _: 1
                }, 8, ["model-value"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(_unref(NkProfile), {
            inline: "",
            data: {
            name: _unref(getCurrentInspectionGroup)?.user?.email,
            date: _unref(getCurrentInspectionGroup)?.date,
          }
          }, null, 8, ["data"]),
          _createVNode(_unref(NkBadge), { size: "sm" }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(getCurrentInspectionGroup)?.service?.name), 1)
            ]),
            _: 1
          })
        ]),
        _createVNode(_unref(NkGroup), { spacing: 5 }, {
          default: _withCtx(() => [
            _createVNode(_unref(NkInlineModal), {
              title: _ctx.$t('inspection-group.action.pdf_report'),
              class: "max-w-3xl"
            }, {
              content: _withCtx(() => [
                _createVNode(_unref(NkGroup), {
                  spacing: 4,
                  "no-wrap": "",
                  align: "flex-start"
                }, {
                  default: _withCtx(() => [
                    _createVNode(NkInspectionPDFForm, {
                      inspectionGroup: _unref(inspectionGroupData).id,
                      class: "basis-1/2"
                    }, null, 8, ["inspectionGroup"]),
                    _createVNode(NkInspectionPDFDownload, {
                      inspectionGroup: _unref(inspectionGroupData).id,
                      class: "basis-1/2"
                    }, null, 8, ["inspectionGroup"])
                  ]),
                  _: 1
                })
              ]),
              default: _withCtx(() => [
                _createVNode(_unref(NkButton), {
                  disabled: !_unref(canExportReport),
                  title: !_unref(canExportReport) ? _ctx.$t('inspection-group.report.not_allowed') : _ctx.$t('inspection-group.report.allowed'),
                  icon: "NkDownload"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('inspection-group.action.pdf_report')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled", "title"])
              ]),
              _: 1
            }, 8, ["title"]),
            _createVNode(_unref(NkInlineModal), {
              title: _ctx.$t('inspection-group.action.export_in_zip'),
              class: "max-w-sm"
            }, {
              content: _withCtx(() => [
                _createVNode(InspectionZIPForm, {
                  inspectionGroup: _unref(inspectionGroupData).id
                }, null, 8, ["inspectionGroup"])
              ]),
              default: _withCtx(() => [
                _createVNode(_unref(NkButton), {
                  disabled: _unref(currentInspectionGroup)?.severityCounter.length > 0 && !_unref(currentInspectionGroup)?.severityCounter.some(e => e !== null),
                  title: _unref(currentInspectionGroup)?.severityCounter.length > 0 && !_unref(currentInspectionGroup)?.severityCounter.some(e => e !== null) ? _ctx.$t('inspection-group.zip.not_allowed') : _ctx.$t('inspection-group.zip.allowed'),
                  icon: "NkStickyNote2"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('inspection-group.action.export_in_zip')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled", "title"])
              ]),
              _: 1
            }, 8, ["title"]),
            _createVNode(_unref(NkGroup), { spacing: 2 }, {
              default: _withCtx(() => [
                (_ctx.$store.getters['connection/isGranted'](['inspection_edit']))
                  ? (_openBlock(), _createBlock(_unref(NkInlineModal), {
                      key: 0,
                      title: _ctx.$t('inspection.action.edit'),
                      class: "max-w-screen-md"
                    }, {
                      content: _withCtx(({close}) => [
                        _createVNode(NkInspectionEditForm, {
                          id: _unref(inspectionQueryData).id,
                          inspectionGroupId: _unref(inspectionGroupData).id,
                          onSubmitted: close
                        }, null, 8, ["id", "inspectionGroupId", "onSubmitted"])
                      ]),
                      default: _withCtx(() => [
                        _createVNode(_unref(NkButton), {
                          plain: "",
                          icon: "NkPencil"
                        })
                      ]),
                      _: 1
                    }, 8, ["title"]))
                  : _createCommentVNode("", true),
                (_ctx.$store.getters['connection/isGranted'](['inspection_group_delete']))
                  ? (_openBlock(), _createBlock(_component_el_popconfirm, {
                      key: 1,
                      title: _ctx.$t('inspection-group.action.delete_confirmation'),
                      onConfirm: handleInspectionGroupDelete
                    }, {
                      reference: _withCtx(() => [
                        _createVNode(_unref(NkButton), {
                          plain: "",
                          type: "danger",
                          icon: "NkDelete"
                        })
                      ]),
                      _: 1
                    }, 8, ["title"]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ], 4))
}
}

})